import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@shared/contexts'

interface UseProtectedActionOptions {
  onUnauthorized?: () => void
}

/**
 * Hook that wraps protected actions with authentication validation
 * Syncs auth with server before executing the action
 * Redirects to login if unauthorized
 */
export function useProtectedAction(options?: UseProtectedActionOptions) {
  const { isAuthenticated, refetchUser } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [isValidating, setIsValidating] = useState(false)

  /**
   * Execute a protected action
   * Validates auth with server before executing
   */
  const executeProtected = async <T>(
    action: () => Promise<T>
  ): Promise<T> => {
    // Check local auth first
    if (!isAuthenticated) {
      // Save current location for redirect after login
      const currentPath = location.pathname + location.search
      localStorage.setItem('prevPathContact', currentPath)

      if (options?.onUnauthorized) {
        options.onUnauthorized()
      } else {
        void navigate('/login', { state: { from: location } })
      }
      throw new Error('Unauthorized')
    }

    // Sync with server
    setIsValidating(true)
    try {
      const user = await refetchUser()

      if (!user) {
        // Auth failed
        const currentPath = location.pathname + location.search
        localStorage.setItem('prevPathContact', currentPath)
        void navigate('/login', { state: { from: location } })
        throw new Error('Unauthorized')
      }

      // Execute the protected action
      const result = await action()
      return result
    } finally {
      setIsValidating(false)
    }
  }

  return {
    executeProtected,
    isValidating,
  }
}
